import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Modal,
  Dimensions,
  ScrollView,
  Platform,
  Animated,
  PanResponder,
} from 'react-native';
import { useTheme } from '../context/ThemeContext';

const { width, height } = Dimensions.get('window');

const Stopwatch = forwardRef((props, ref) => {
  const theme = useTheme();
  const [showIcon, setShowIcon] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [time, setTime] = useState(0); // Time in milliseconds
  const [laps, setLaps] = useState([]);
  const intervalRef = useRef(null);
  const startTimeRef = useRef(0);
  const pausedTimeRef = useRef(0);

  useEffect(() => {
    if (isRunning && !isPaused) {
      intervalRef.current = setInterval(() => {
        const now = Date.now();
        const elapsed = now - startTimeRef.current + pausedTimeRef.current;
        setTime(elapsed);
      }, 10); // Update every 10ms for smooth display
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isRunning, isPaused]);

  // Drag functionality
  const pan = useRef(new Animated.ValueXY()).current;
  const [showDeleteZone, setShowDeleteZone] = useState(false);
  const iconSize = 60;
  const deleteZoneSize = 80;

  // PanResponder for drag functionality
  const panResponder = useRef(
    PanResponder.create({
      onStartShouldSetPanResponder: () => true,
      onMoveShouldSetPanResponder: () => true,
      onPanResponderGrant: () => {
        setShowDeleteZone(true);
        pan.setOffset({
          x: pan.x._value,
          y: pan.y._value,
        });
      },
      onPanResponderMove: (evt, gestureState) => {
        pan.setValue({ x: gestureState.dx, y: gestureState.dy });
        
        // Check if icon is over delete zone (bottom center)
        const iconY = height / 2 - iconSize / 2 + gestureState.dy;
        const iconX = width - iconSize / 2 + gestureState.dx;
        const deleteZoneY = height - deleteZoneSize - 20;
        const deleteZoneX = width / 2;
        
        const distance = Math.sqrt(
          Math.pow(iconX - deleteZoneX, 2) + Math.pow(iconY - deleteZoneY, 2)
        );
        
        if (distance < deleteZoneSize) {
          // Icon is over delete zone
          setShowDeleteZone(true);
        }
      },
      onPanResponderRelease: (evt, gestureState) => {
        pan.flattenOffset();
        
        // Check if dropped on delete zone
        const iconY = height / 2 - iconSize / 2 + gestureState.dy;
        const iconX = width - iconSize / 2 + gestureState.dx;
        const deleteZoneY = height - deleteZoneSize - 20;
        const deleteZoneX = width / 2;
        
        const distance = Math.sqrt(
          Math.pow(iconX - deleteZoneX, 2) + Math.pow(iconY - deleteZoneY, 2)
        );
        
        if (distance < deleteZoneSize) {
          // Dropped on delete zone - remove stopwatch
          setShowIcon(false);
          setShowModal(false);
          setIsMinimized(false);
          setTime(0);
          setIsRunning(false);
          setIsPaused(false);
          startTimeRef.current = 0;
          pausedTimeRef.current = 0;
          setLaps([]);
          pan.setValue({ x: 0, y: 0 });
        } else {
          // Return to original position
          Animated.spring(pan, {
            toValue: { x: 0, y: 0 },
            useNativeDriver: false,
          }).start();
        }
        
        setShowDeleteZone(false);
      },
    })
  ).current;

  const formatTime = (ms) => {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    const milliseconds = Math.floor((ms % 1000) / 10);
    
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}:${String(milliseconds).padStart(2, '0')}`;
  };

  const handleStart = () => {
    if (!isRunning) {
      startTimeRef.current = Date.now();
      setIsRunning(true);
      setIsPaused(false);
    }
  };

  const handleStop = () => {
    if (isRunning) {
      pausedTimeRef.current += Date.now() - startTimeRef.current;
      setIsRunning(false);
      setIsPaused(true);
    }
  };

  const handleResume = () => {
    if (isPaused) {
      startTimeRef.current = Date.now();
      setIsRunning(true);
      setIsPaused(false);
    }
  };

  const handleReset = () => {
    setTime(0);
    setIsRunning(false);
    setIsPaused(false);
    startTimeRef.current = 0;
    pausedTimeRef.current = 0;
    setLaps([]);
  };

  const handleLap = () => {
    if (isRunning) {
      const lapTime = time;
      setLaps(prev => [...prev, { id: Date.now(), time: lapTime }]);
    }
  };

  const handleMinimize = () => {
    setIsMinimized(true);
    setShowModal(false);
  };

  const handlePauseFromToast = () => {
    handleStop();
  };

  const handleResumeFromToast = () => {
    handleResume();
  };

  const handleCloseToast = () => {
    handleReset();
    setIsMinimized(false);
    setShowIcon(true);
  };

  const handleIconPress = () => {
    setShowIcon(false);
    setShowModal(true);
    setIsMinimized(false);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setShowIcon(true);
  };

  // Expose methods via ref
  useImperativeHandle(ref, () => ({
    showIcon: () => {
      setShowIcon(true);
      setIsMinimized(false);
      setShowModal(false);
    },
    showModal: () => {
      setShowIcon(false);
      setShowModal(true);
      setIsMinimized(false);
    },
  }));

  const styles = getStyles(theme);

  return (
    <>
      {/* Delete Zone (X icon at bottom center) */}
      {showDeleteZone && (
        <View style={styles.deleteZone}>
          <Text style={styles.deleteZoneText}>✕</Text>
        </View>
      )}

      {/* Floating Stopwatch Icon */}
      {showIcon && !isMinimized && (
        <Animated.View
          style={[
            styles.floatingIcon,
            {
              transform: [{ translateX: pan.x }, { translateY: pan.y }],
            },
          ]}
          {...panResponder.panHandlers}
        >
          <TouchableOpacity
            onPress={handleIconPress}
            activeOpacity={0.8}
            style={styles.iconTouchable}
          >
            <Text style={styles.iconText}>⏱️</Text>
          </TouchableOpacity>
        </Animated.View>
      )}

      {/* Minimized Toast */}
      {isMinimized && (
        <View style={styles.toastContainer}>
          <View style={styles.toastContent}>
            <Text style={styles.toastIcon}>⏱️</Text>
            <Text style={styles.toastTime}>{formatTime(time)}</Text>
            <View style={styles.toastButtons}>
              {isRunning && !isPaused ? (
                <>
                  <TouchableOpacity
                    style={styles.toastButton}
                    onPress={() => {}}
                    activeOpacity={0.7}
                    disabled={true}
                  >
                    <Text style={styles.toastButtonText}>⏱️</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={styles.toastButton}
                    onPress={handlePauseFromToast}
                    activeOpacity={0.7}
                  >
                    <Text style={styles.toastButtonText}>⏸</Text>
                  </TouchableOpacity>
                </>
              ) : (
                <>
                  <TouchableOpacity
                    style={styles.toastButton}
                    onPress={handleCloseToast}
                    activeOpacity={0.7}
                  >
                    <Text style={styles.toastButtonText}>✕</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={styles.toastButton}
                    onPress={handleResumeFromToast}
                    activeOpacity={0.7}
                  >
                    <Text style={styles.toastButtonText}>▶</Text>
                  </TouchableOpacity>
                </>
              )}
            </View>
          </View>
        </View>
      )}

      {/* Main Modal */}
      <Modal
        visible={showModal}
        transparent={true}
        animationType="slide"
        onRequestClose={handleCloseModal}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            {/* Close Button */}
            <TouchableOpacity
              style={styles.closeButton}
              onPress={handleCloseModal}
              activeOpacity={0.7}
            >
              <Text style={styles.closeButtonText}>✕</Text>
            </TouchableOpacity>

            {/* Timer Display */}
            <View style={styles.timerContainer}>
              <Text style={styles.timerText}>{formatTime(time)}</Text>
            </View>

            {/* Laps List */}
            {laps.length > 0 && (
              <View style={styles.lapsContainer}>
                <ScrollView style={styles.lapsScrollView}>
                  {laps.map((lap, index) => (
                    <View key={lap.id} style={styles.lapItem}>
                      <Text style={styles.lapNumber}>Lap {laps.length - index}</Text>
                      <Text style={styles.lapTime}>{formatTime(lap.time)}</Text>
                    </View>
                  ))}
                </ScrollView>
              </View>
            )}

            {/* Control Buttons */}
            <View style={styles.controlsContainer}>
              <TouchableOpacity
                style={[
                  styles.controlButton,
                  styles.lapButton,
                  (!isRunning || isPaused) && styles.buttonDisabled,
                ]}
                onPress={isRunning && !isPaused ? handleLap : handleReset}
                disabled={!isRunning && !isPaused}
                activeOpacity={0.7}
              >
                <Text style={[
                  styles.controlButtonText,
                  (!isRunning || isPaused) && styles.buttonDisabledText,
                ]}>
                  {isRunning && !isPaused ? 'Lap' : isPaused ? 'Reset' : 'Lap'}
                </Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[
                  styles.controlButton,
                  styles.startStopButton,
                  isRunning ? styles.stopButton : styles.startButton,
                ]}
                onPress={isRunning ? handleStop : isPaused ? handleResume : handleStart}
                activeOpacity={0.7}
              >
                <Text style={[
                  styles.controlButtonText,
                  !isRunning && !isPaused && styles.startButtonText,
                ]}>
                  {isRunning ? 'Stop' : isPaused ? 'Resume' : 'Start'}
                </Text>
              </TouchableOpacity>
            </View>

            {/* Minimize Button */}
            <TouchableOpacity
              style={styles.minimizeButton}
              onPress={handleMinimize}
              activeOpacity={0.7}
            >
              <Text style={styles.minimizeButtonText}>Minimise</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    </>
  );
});

export default Stopwatch;

const getStyles = (theme) => StyleSheet.create({
  floatingIcon: {
    position: 'absolute',
    right: 10,
    top: height / 2 - 30,
    width: 60,
    height: 60,
    zIndex: 1000,
  },
  iconTouchable: {
    width: 60,
    height: 60,
    borderRadius: 30,
    backgroundColor: theme.isDarkMode ? '#1A1A1A' : '#FFFFFF',
    justifyContent: 'center',
    alignItems: 'center',
    elevation: 5,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
  },
  iconText: {
    fontSize: 30,
  },
  deleteZone: {
    position: 'absolute',
    bottom: 20,
    left: width / 2 - 40,
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: '#FF4444',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1001,
    elevation: 10,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.5,
    shadowRadius: 8,
  },
  deleteZoneText: {
    fontSize: 40,
    color: '#FFFFFF',
    fontWeight: 'bold',
  },
  toastContainer: {
    position: 'absolute',
    bottom: 20,
    left: width * 0.05,
    width: width * 0.9,
    height: 150,
    zIndex: 1001,
  },
  toastContent: {
    flex: 1,
    flexDirection: 'row',
    backgroundColor: theme.isDarkMode ? '#1A1A1A' : '#FFFFFF',
    borderRadius: 20,
    padding: 15,
    alignItems: 'center',
    justifyContent: 'space-between',
    borderWidth: 1,
    borderColor: theme.colors.border || '#E0E0E0',
    elevation: 5,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
  },
  toastIcon: {
    fontSize: 30,
    marginRight: 15,
  },
  toastTime: {
    flex: 1,
    fontSize: 24,
    fontWeight: '600',
    color: theme.colors.text,
    textAlign: 'center',
  },
  toastButtons: {
    flexDirection: 'row',
    gap: 10,
  },
  toastButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: theme.isDarkMode ? '#2A2A2A' : '#F0F0F0',
    justifyContent: 'center',
    alignItems: 'center',
  },
  toastButtonText: {
    fontSize: 18,
    color: theme.colors.text,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    width: width * 0.9,
    maxHeight: height * 0.8,
    backgroundColor: theme.isDarkMode ? '#1A1A1A' : '#FFFFFF',
    borderRadius: 20,
    padding: 20,
    alignItems: 'center',
  },
  closeButton: {
    position: 'absolute',
    top: 15,
    right: 15,
    width: 30,
    height: 30,
    borderRadius: 15,
    backgroundColor: theme.isDarkMode ? '#2A2A2A' : '#F0F0F0',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10,
  },
  closeButtonText: {
    fontSize: 18,
    color: theme.colors.text,
    fontWeight: 'bold',
  },
  timerContainer: {
    marginTop: 20,
    marginBottom: 30,
    alignItems: 'center',
  },
  timerText: {
    fontSize: 48,
    fontWeight: 'bold',
    color: theme.colors.heading || '#FDFD0A',
    fontFamily: Platform.OS === 'ios' ? 'Courier' : 'monospace',
  },
  lapsContainer: {
    width: '100%',
    maxHeight: 200,
    marginBottom: 20,
  },
  lapsScrollView: {
    maxHeight: 200,
  },
  lapItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 10,
    paddingHorizontal: 15,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border || '#E0E0E0',
  },
  lapNumber: {
    fontSize: 16,
    color: theme.colors.text,
  },
  lapTime: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.text,
    fontFamily: Platform.OS === 'ios' ? 'Courier' : 'monospace',
  },
  controlsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    width: '100%',
    marginBottom: 20,
  },
  controlButton: {
    paddingVertical: 15,
    paddingHorizontal: 40,
    borderRadius: 25,
    minWidth: 100,
    alignItems: 'center',
  },
  lapButton: {
    backgroundColor: theme.isDarkMode ? '#2A2A2A' : '#E0E0E0',
  },
  startButton: {
    backgroundColor: theme.colors.heading || '#FDFD0A',
  },
  stopButton: {
    backgroundColor: '#FF4444',
  },
  buttonDisabled: {
    backgroundColor: theme.isDarkMode ? '#1A1A1A' : '#F0F0F0',
    opacity: 0.5,
  },
  controlButtonText: {
    fontSize: 18,
    fontWeight: '600',
    color: theme.isDarkMode ? '#FFFFFF' : '#000000',
  },
  startButtonText: {
    color: '#000000', // Black text for Start button (yellow background)
  },
  buttonDisabledText: {
    color: theme.isDarkMode ? '#666666' : '#999999',
  },
  minimizeButton: {
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 15,
    backgroundColor: theme.isDarkMode ? '#2A2A2A' : '#E0E0E0',
  },
  minimizeButtonText: {
    fontSize: 16,
    color: theme.colors.text,
  },
});
